import React, { useCallback, useMemo } from "react";
import * as TableRenderers from "../shared/renderers/table";
import { BooleanRenderer, mapRenderer, StringRenderer } from "../shared/renderers/table";
import { FormConfiguration } from "../shared/crud/form";
import SimpleCrud, { defaultCrudMessages } from "../shared/crud/simple-crud";
import { translationFieldConfig } from "../shared/widgets/translation";
import originalSchema from "../../gen/schema/witbooking.backoffice.web_message.v1/WebMessage.json";
import { usePropertyLocales } from "../../components/property-locale-provider";
import { getLanguageFromFullLocale } from "../../lib/config";
import actions from "../../lib/actions";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { SECTION } from "../../lib/constants";
import {
  tableHeaderMessages,
  webMessageMessages,
  WebMessagePositionType,
  WebMessagePositionTypeMapping,
  webMessagePositionTypeMessages,
  WebMessageType,
  webMessageTypeMessages,
} from "./messages";
import { SwitchGroup } from "../shared/widgets/SwitchGroup";
import { v4 as uuidv4 } from "uuid";
import { TableColumns } from "../shared/crud/list";
import { WebMessageEntity } from "./domain/webMessage.entity";
import { useProperty } from "../../components/property-provider";
import {
  createEntity,
  deleteEntity,
  getAllEntities,
  getEntity,
  updateEntity,
} from "./infrastructure/webMessage.api";
import { DateRangePicker } from "../shared/widgets/dateRangePicker";
import SectionUnderline from "../shared/widgets/sectionUnderline";
import { HtmlEditorWidget } from "../shared/widgets/html";
import { DateRangeStringRenderer } from "../shared/widgets/DateRangeStringRenderer";
import { WebMessagePositionType as WebMessagePositionTypeProto } from "../../gen/witbooking/backoffice/webmessage/v1/webmessage_api_pb";
import { WebMessageType as WebMessageTypeProto } from "../../gen/witbooking/backoffice/webmessage/v1/webmessage_api_pb";
import { FormattedMessage, useIntl } from "react-intl";
import { Alert } from "react-bootstrap";

const schema = {
  ...originalSchema,
  properties: {
    ...originalSchema.properties,
    validitySection: {
      type: "string",
    },
    positionType: {
      enum: [
        "TOP_INVENTORY_STEP_1",
        1,
        "MODAL_STEP_1",
        5,
        "MODAL_STEP_2",
        6,
        "BELOW_PERSONAL_INFO_STEP_2",
        3,
        "TOP_METHOD_PAYMENT_STEP_2",
        4,
        "CONFIRMATION_PAGE",
        7,
      ],
      oneOf: [
        {
          type: "string",
        },
        {
          type: "integer",
        },
      ],
    },
    type: {
      enum: ["INFO", 1, "NOTICE", 2, "ERROR", 3, "SUCCESS", 4],
      oneOf: [
        {
          type: "string",
        },
        {
          type: "integer",
        },
      ],
    },
  },
};

const messages = {
  ...defaultCrudMessages,
  ...webMessageMessages,
  ...webMessageTypeMessages,
  ...webMessagePositionTypeMessages,
  tableHeaders: tableHeaderMessages,
  warning: {
    id: "webMessages.warning",
    defaultMessage: "Taxes and Citytax must be create in Settings > Parameterization > Rates. To avoid duplicates, please Do NOT include this information in Web messages.",
  }
};
interface WebMessageFilters {
  ticker: string;
}
function buildFormConfiguration(allowedLocales: string[]): FormConfiguration<WebMessageEntity> {
  return {
    __fieldOrder: [
      "id",
      "active",
      "translationsList",
      "positionType",
      "type",
      "unlockCode",
      "showOnlyOnUnavailable",
      "validitySection",
      "bookingWindowPeriod",
      "*",
    ],
    __useProtoStyleArrays: true,
    id: {
      default: () => uuidv4(),
      hidden: true,
    },
    active: {
      widget: SwitchGroup,
      messages: {
        label: messages.active,
      },
    },
    positionType: {
      messages: {
        label: messages.webMessagePositionType,
      },
      default: () => WebMessagePositionTypeProto.TOP_INVENTORY_STEP_1,
      enumLabels: [
        messages[WebMessagePositionType.TOP_INVENTORY_STEP_1],
        messages[WebMessagePositionType.MODAL_STEP_1],
        messages[WebMessagePositionType.MODAL_STEP_2],
        messages[WebMessagePositionType.BELOW_PERSONAL_INFO_STEP_2],
        messages[WebMessagePositionType.TOP_METHOD_PAYMENT_STEP_2],
        messages[WebMessagePositionType.CONFIRMATION_PAGE],
      ],
    },
    type: {
      messages: {
        label: messages.type,
      },
      default: () => WebMessageTypeProto.INFO,
      hidden: true,
      enumLabels: [
        messages[WebMessageType.INFO],
        messages[WebMessageType.NOTICE],
        messages[WebMessageType.ERROR],
        messages[WebMessageType.SUCCESS],
      ],
    },
    translationsList: {
      ...translationFieldConfig(allowedLocales || []),
      children: {
        __fieldOrder: ["title", "description", "*"],
        description: {
          widget: HtmlEditorWidget,
          messages: {
            label: messages.webMessageTranslationsDescriptionLabel,
          },
        },
        title: { hidden: true },
        ...translationFieldConfig(allowedLocales || []).children,
      },
      messages: {
        label: messages.webMessageTranslationsTitle,
      },
    },
    showOnlyOnUnavailable: {
      widget: SwitchGroup,
      messages: {
        label: messages.showOnlyOnUnavailable,
      },
    },
    validitySection: {
      fieldWidget: SectionUnderline,
      messages: {
        label: messages.validity,
      },
    },
    unlockCode: {
      messages: {
        label: messages.unlockCode,
      },
    },
    bookingWindowPeriod: {
      fieldWidget: DateRangePicker,
    },
  };
}
function WebMessageCRUD() {
  const allowedLocales = usePropertyLocales();
  const propertyInfo = useProperty();
  const intl = useIntl();

  const onGetAll = useCallback(async () => getAllEntities(propertyInfo.token), [
    propertyInfo.token,
  ]);
  const onDelete = useCallback(async (id: string) => deleteEntity(id, propertyInfo.token), [
    propertyInfo.token,
  ]);
  const onGetOne = useCallback(async (id: string) => getEntity(id, propertyInfo.token), [
    propertyInfo.token,
  ]);
  const onCreate: (entity: WebMessageEntity) => Promise<string> = useCallback(
    async entity => createEntity(entity, propertyInfo.token),
    [propertyInfo.token]
  );
  const onUpdate: (entity: WebMessageEntity) => Promise<void> = useCallback(
    async entity => updateEntity(entity, propertyInfo.token),
    [propertyInfo.token]
  );

  const tableColumns = useMemo(() => buildTableColumns(), []);
  const formConfiguration = useMemo(() => buildFormConfiguration(allowedLocales), [allowedLocales]);
  const validate = useCallback(
    (formData, errors) => {
      if (
        !formData.bookingWindowPeriod?.from ||
        !formData.bookingWindowPeriod?.to ||
        formData.bookingWindowPeriod?.to <= formData.bookingWindowPeriod?.from
      ) {
        errors.bookingWindowPeriod.addError(intl.formatMessage(messages.validityError));
      }
      return errors;
    },
    [intl]
  );

  if (allowedLocales === undefined) {
    return null;
  }

  return (
    <>
      <Alert variant="danger">
        <strong>
          <FormattedMessage {...messages.warning} />
        </strong>
      </Alert>
      <SimpleCrud<WebMessageEntity, WebMessageFilters>
        formConfiguration={formConfiguration}
        schema={schema}
        tableColumns={tableColumns}
        messages={messages}
        onGetAll={onGetAll}
        onGetOne={onGetOne}
        onCreate={onCreate}
        onUpdate={onUpdate}
        onDelete={onDelete}
        hideExportButton
        canCreate={actions.CreateWebMessage}
        canUpdate={actions.ModifyWebMessage}
        section={SECTION.WEB_MESSAGES}
        listHeight={"100%"}
        validate={validate}
      />
    </>
);
}
function buildTableColumns(): TableColumns<WebMessageEntity> {
  return {
    active: {
      renderer: BooleanRenderer,
      width: 100,
    },
    positionType: {
      renderer: mapRenderer(StringRenderer, (v, intl) =>
        intl.formatMessage(messages[WebMessagePositionTypeMapping[v]])
      ),
      width: 250,
    },
    translationsList: {
      renderer: TableRenderers.mapRenderer(
        TableRenderers.StringRenderer,
        (v, intl) => {
          if (!v || v.length === 0) return undefined;
          const current = v.find(t => t.locale === getLanguageFromFullLocale(intl.locale));
          return current?.description || v[0].description;
        },
        { isHtml: true, maxCharacters: 300 }
      ),
    },
    showOnlyOnUnavailable: {
      renderer: BooleanRenderer,
      width: 250,
    },
    bookingWindowPeriod: {
      renderer: DateRangeStringRenderer,
      width: 200,
    },
  };
}

export default withAuthorizationVerifier(WebMessageCRUD);
